/*
 * PE Savings Slider
 *
 * Drives sections/pe-savings-slider.liquid.
 *
 * Looping is done by rotating the track, not by scrolling back to the start.
 * A modulo index would make "next" on the last slide scroll left across every
 * slide to get back to the first one, which reads as the wrong direction. Here
 * the slide order itself moves, so next always travels right and previous
 * always travels left, and there is no end to hit.
 *
 * The invariant is: at rest, the visible slide is the track's first child and
 * scrollLeft is 0.
 *
 *   next      settle, then scroll right one width. The settle handler rotates
 *             the leading slide to the back and resets scrollLeft to 0 — the
 *             visible slide does not change while that happens, so it cannot
 *             be seen.
 *   previous  settle, move the trailing slide to the front and add one width
 *             to scrollLeft (identical pixels on screen, but now there is a
 *             slide to the left), then animate to 0.
 *
 * The viewport is a scroll-snap container, so swipe, trackpad and keyboard
 * scrolling all work with this file absent; the rotation is re-established
 * from whatever position the reader lands on. Remove this file and the section
 * degrades to a swipeable strip rather than breaking.
 *
 * Height. The track is `align-items: flex-start`, so each slide keeps its own
 * height and the viewport would otherwise be as tall as the tallest one. When
 * the section sets data-adapt-height this element writes an explicit pixel
 * height for whichever slide is on screen, and the stylesheet eases it, so the
 * section grows and shrinks with the slide instead of padding the short ones out
 * with empty space.
 *
 * The height is set at the same moment the horizontal scroll starts — against
 * the INCOMING slide, not the one leaving — so both animate together rather than
 * the box resizing after the slide has already arrived. A ResizeObserver keeps
 * it honest as fonts and images land and as the viewport is resized.
 */

if (!customElements.get('pe-savings-slider')) {
  customElements.define(
    'pe-savings-slider',
    class PeSavingsSlider extends HTMLElement {
      connectedCallback() {
        if (this.dataset.peReady) return;

        this.viewport = this.querySelector('[data-pe-viewport]');
        this.track = this.querySelector('[data-pe-track]');
        this.prevButton = this.querySelector('[data-pe-prev]');
        this.nextButton = this.querySelector('[data-pe-next]');

        const slideCount = this.querySelectorAll('[data-pe-slide]').length;

        /* One slide is not a slider. The arrows are absolutely positioned, so
           hiding them leaves the heading exactly where it was. */
        if (!this.viewport || !this.track || slideCount < 2) {
          this.prevButton?.setAttribute('hidden', '');
          this.nextButton?.setAttribute('hidden', '');
          return;
        }

        this.dataset.peReady = 'true';

        this.prevButton?.addEventListener('click', () => this.previous());
        this.nextButton?.addEventListener('click', () => this.next());

        if (this.dataset.adaptHeight === 'true') {
          this.adaptHeight = true;

          /* First measurement waits a frame, so the browser has laid the slides
             out at least once. Assigning over `auto` does not animate, which is
             what keeps the section from easing open on load. */
          requestAnimationFrame(() => this.applyHeight());

          /* Fonts and images change a slide's height after that first frame, and
             a viewport resize reflows every slide. Rather than guess at either,
             re-measure whenever a slide actually changes size. Observing all of
             them avoids having to re-target the observer every rotation. */
          if ('ResizeObserver' in window) {
            this.observer = new ResizeObserver(() => {
              cancelAnimationFrame(this.measureFrame);
              this.measureFrame = requestAnimationFrame(() => this.applyHeight());
            });
            this.querySelectorAll('[data-pe-slide]').forEach((slide) => this.observer.observe(slide));
          }
        }

        /* Re-establish the invariant after any settled scroll, whoever caused
           it — an arrow, a swipe, a trackpad, or arrow keys on the focused
           viewport. Smooth scrolling fires continuously, so this only lands
           once movement has actually stopped. */
        this.viewport.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.settleTimer);
            this.settleTimer = setTimeout(() => this.settle(), 140);
          },
          { passive: true }
        );

        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        clearTimeout(this.settleTimer);
        cancelAnimationFrame(this.measureFrame);
        this.observer?.disconnect();
      }

      /* At rest the slide on screen is always the track's first child - that is
         the invariant the rotation maintains. */
      get currentSlide() {
        return this.track?.firstElementChild ?? null;
      }

      /*
       * Pin the viewport to a slide's height. Called with the INCOMING slide when
       * a move starts, and with no argument once things have settled.
       *
       * getBoundingClientRect rather than offsetHeight: the height is fractional
       * often enough that rounding down leaves a pixel of the last row clipped.
       * A zero height means the slide is not laid out yet, so it is ignored - the
       * ResizeObserver will call back when it is.
       */
      applyHeight(slide) {
        if (!this.adaptHeight) return;

        const target = slide || this.currentSlide;
        if (!target) return;

        const height = target.getBoundingClientRect().height;
        if (height > 0) this.viewport.style.height = `${Math.ceil(height)}px`;
      }

      get reduceMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      get step() {
        return this.viewport.clientWidth;
      }

      /* Structural moves must not animate, or the reader sees the track jump.
         scrollBehavior is forced to auto for the duration, then handed back to
         the stylesheet. */
      withoutAnimation(callback) {
        const previous = this.viewport.style.scrollBehavior;
        this.viewport.style.scrollBehavior = 'auto';
        callback();
        this.viewport.style.scrollBehavior = previous;
      }

      /*
       * Rotate whatever slide the reader is on to the front of the track and
       * reset scrollLeft to 0, instantly. Always ends on the invariant.
       *
       * `mode` decides how a position part-way between two slides is resolved,
       * which is what makes a second arrow press during an in-flight animation
       * queue another move instead of being swallowed:
       *
       *   'forward'  round up   — a press while gliding right lands one further
       *   'back'     round down — the mirror image for a press while gliding left
       *   default    round to nearest — for a swipe the reader has let go of
       *
       * The small epsilon keeps sub-pixel scroll offsets at rest from reading
       * as a partial move in either direction.
       */
      settle(mode) {
        const step = this.step;
        if (!step) return;

        const exact = this.viewport.scrollLeft / step;
        let steps;
        if (mode === 'forward') steps = Math.ceil(exact - 0.02);
        else if (mode === 'back') steps = Math.floor(exact + 0.02);
        else steps = Math.round(exact);

        steps = Math.max(0, steps);

        this.withoutAnimation(() => {
          for (let i = 0; i < steps; i += 1) {
            this.track.appendChild(this.track.firstElementChild);
          }
          this.viewport.scrollLeft = 0;
        });

        /* Whatever caused the movement - arrow, swipe, trackpad, keyboard - the
           slide on screen has just been rotated to the front, so this is the one
           place that always knows the final height. */
        this.applyHeight();
      }

      next() {
        clearTimeout(this.settleTimer);
        this.settle('forward');

        /* settle() has left the visible slide at the front, so the one being
           scrolled to is the second child. Setting the height against it now
           means the box and the scroll ease together. */
        this.applyHeight(this.track.children[1]);

        this.viewport.scrollTo({
          left: this.step,
          behavior: this.reduceMotion ? 'auto' : 'smooth',
        });

        /* With motion reduced there is no animation to wait on, and the scroll
           event may not fire at all, so close the loop here. */
        if (this.reduceMotion) this.settle();

        this.restartAutoplay();
      }

      previous() {
        clearTimeout(this.settleTimer);
        this.settle('back');

        /* Put the last slide in front and hold the same pixels on screen.
           Writing scrollLeft right after the move forces the layout to flush,
           so the animated scroll below starts from the corrected position. */
        this.withoutAnimation(() => {
          this.track.insertBefore(this.track.lastElementChild, this.track.firstElementChild);
          this.viewport.scrollLeft = this.step;
        });

        /* The incoming slide is the one just moved to the front. */
        this.applyHeight();

        this.viewport.scrollTo({
          left: 0,
          behavior: this.reduceMotion ? 'auto' : 'smooth',
        });

        this.restartAutoplay();
      }

      /* A deliberate press means the reader is driving. Reset the countdown so
         the slide does not move again a moment later. */
      restartAutoplay() {
        if (this.timer) this.startAutoplay();
      }

      startAutoplay() {
        this.stopAutoplay();

        const seconds = parseFloat(this.dataset.autoplaySpeed);
        if (this.dataset.autoplay !== 'true' || !seconds || this.reduceMotion) return;

        this.timer = setInterval(() => this.next(), seconds * 1000);

        /* Pause while the reader is engaged — hover and keyboard focus alike —
           and resume once they leave. Bound once, since startAutoplay runs
           again every time the countdown is reset. */
        if (!this.pauseBound) {
          this.pauseBound = true;
          ['mouseenter', 'focusin'].forEach((event) =>
            this.addEventListener(event, () => this.stopAutoplay())
          );
          ['mouseleave', 'focusout'].forEach((event) =>
            this.addEventListener(event, () => {
              if (!this.contains(document.activeElement)) this.startAutoplay();
            })
          );
        }
      }

      stopAutoplay() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
}
